import React from "react";
import { observer } from "mobx-react";
import { Redirect, Route, useLocation } from "react-router-dom";
import { PrivateRoute } from "../navigation";

import { ReactLazyImportWithGuard as lazy } from "@lib/lazy-import-with-guard";
const loadFailPath = `/${getCurrentCulture()}/app-update`;
// prettier-ignore
const MobileError404 = lazy(loadFailPath, ()=>import("@components-shared/mobile/error/Error404"));
// prettier-ignore
const DesktopError404 = lazy(loadFailPath, ()=>import("@components-shared/desktop/error/Error404"));

import { DeviceDetector, getCurrentCulture } from "@utils";
import { isCapacitorPlatform } from "@utils/specific/capacitor";
import { DefaultPath } from "@utils/common/defaultRoutePathsEnum";
import { getRouteWithReturnUrl } from "@helpers/shared/getRouteWithReturnUrl";
import { useRootAppStore } from "@hooks";
import { useAppContext } from "@state/hooks/shared";
import { RequiredUserAction } from "@state/auth/types";
import { messageDisplayTypes } from "@utils/auth";

export const RouteElement = observer(function RouteElement(props: {
	exact?: boolean;
	privateRoute?: boolean;
	path: string | string[];
	children: JSX.Element | JSX.Element[] | string | null;
}) {
	const { privateRoute, path, children, ...rest } = props;
	const { culture } = useAppContext();
	const userStore = useRootAppStore().userAuthStore;

	const { pathname, search } = useLocation();

	const isPaymentRoute =
		pathname.includes("/deposit") ||
		pathname.includes("/withdrawal") ||
		pathname.includes("/automatic-withdrawal") ||
		pathname.includes("/account-settings/my-bonuses") ||
		pathname.includes("/user-profile-recheck") ||
		pathname.includes("/limit-increase");

	const isCasinoRoute =
		pathname.includes("/games") || pathname.includes("/live-games");
	const accountVerification = pathname.includes("/account-verification");
	const path404 = pathname + search;

	if (
		(isPaymentRoute || isCasinoRoute || accountVerification) &&
		userStore.user != null &&
		!userStore.isInternetUser &&
		!pathname.includes("/my-bonuses")
	) {
		if (DeviceDetector.isMobileTheme) {
			return <MobileError404 path404={path404} />;
		}

		return <DesktopError404 path404={path404} />;
	}

	const bettingActivityUrl = `/${culture}/auth/betting-activity`;
	const accountActivationUrl = `/${culture}/auth/account-activation`;
	const accountActivationCongratulationsUrl = `/${culture}/auth/account-activation-congratulations`;
	const forcePasswordChangeUrl = `/${culture}/auth/force-password-change`;
	const acceptNewTermsUrl = `/${culture}/auth/accept-new-terms`;
	const loginUrl = `/${culture}/auth/login`;
	const registrationUrl = `/${culture}/auth/register`;
	const homeUrl = `/${culture}/${DefaultPath.HOME}`;
	const forgotPasswordUrl = `/${culture}/auth/forgot-password`;
	const forgotUsernameUrl = `/${culture}/auth/forgot-username`;
	const passwordRecoveryUrl = `/${culture}/auth/password-recovery`;
	const confirmUserDataUrl = `/${culture}/auth/confirm-user-data`;
	const userMatchingUrl = `/${culture}/auth/match-user`;
	const acceptGdprUrl = `/${culture}/auth/accept-gdpr`;

	const partnerRegistrationUrl = `/${culture}/auth/register/partner`;

	const doesRouteRequireUserAction =
		((pathname.includes(registrationUrl) || pathname.includes(loginUrl)) &&
			!search.includes("returnUrl")) ||
		pathname.includes(bettingActivityUrl) ||
		pathname.includes(accountActivationUrl) ||
		pathname.includes(accountActivationCongratulationsUrl) ||
		pathname.includes(forcePasswordChangeUrl) ||
		pathname.includes(acceptNewTermsUrl) ||
		pathname.includes(forgotPasswordUrl) ||
		pathname.includes(forgotUsernameUrl) ||
		pathname.includes(passwordRecoveryUrl) ||
		pathname.includes(confirmUserDataUrl) ||
		pathname.includes(userMatchingUrl) ||
		pathname.includes(acceptGdprUrl) ||
		pathname.includes(partnerRegistrationUrl);

	const next = userStore.loginStore.nextRequiredUserAction;

	const params = new URLSearchParams(search);
	const numberOfParams = Array.from(params.keys()).length;
	// if your returnUrl has additional search params such as ?returnUrl=/page/submit&user=2312&isSomething=true
	// params.get("returnUrl") returns only /page/submit without all additional parameters
	const returnUrl = params.get("returnUrl");

	const eventId = params.get("eventId");
	const tipId = params.get("tipId");

	// both will only be true when user comes from next.tipwin
	if (eventId && tipId && DeviceDetector.isMobileTheme) {
		return (
			<Redirect
				to={{
					pathname: `/${getCurrentCulture()}/sports/full/event/${JSON.parse(eventId)}`,
					search: `?tipId=${tipId}`,
				}}
			/>
		);
	}

	if (
		next === RequiredUserAction.ConfirmUserData &&
		!pathname.includes(confirmUserDataUrl)
	) {
		return (
			<Redirect to={getRouteWithReturnUrl(confirmUserDataUrl, search)} />
		);
	}
	if (
		next === RequiredUserAction.AccountActivation &&
		!pathname.includes(accountActivationUrl)
	) {
		return (
			<Redirect
				to={getRouteWithReturnUrl(accountActivationUrl, search)}
			/>
		);
	}
	if (
		next === RequiredUserAction.AccActivationCongratulation &&
		!pathname.includes(accountActivationCongratulationsUrl)
	) {
		return (
			<Redirect
				to={getRouteWithReturnUrl(
					accountActivationCongratulationsUrl,
					search
				)}
			/>
		);
	}
	if (
		next === RequiredUserAction.PasswordChange &&
		!pathname.includes(forcePasswordChangeUrl)
	) {
		return (
			<Redirect
				to={getRouteWithReturnUrl(forcePasswordChangeUrl, search)}
			/>
		);
	}

	if (
		next === RequiredUserAction.UserMatching &&
		!pathname.includes(userMatchingUrl)
	) {
		return <Redirect to={getRouteWithReturnUrl(userMatchingUrl, search)} />;
	}

	if (
		next === RequiredUserAction.BettingActivity &&
		!pathname.includes(bettingActivityUrl)
	) {
		return (
			<Redirect to={getRouteWithReturnUrl(bettingActivityUrl, search)} />
		);
	}

	if (
		next === RequiredUserAction.AcceptNewTerms &&
		!pathname.includes(acceptNewTermsUrl)
	) {
		return (
			<Redirect to={getRouteWithReturnUrl(acceptNewTermsUrl, search)} />
		);
	}

	if (
		next === RequiredUserAction.AcceptGdpr &&
		!pathname.includes(acceptGdprUrl) &&
		!isCapacitorPlatform()
	) {
		//currently disabled for capacitor platform
		return <Redirect to={getRouteWithReturnUrl(acceptGdprUrl, search)} />;
	}

	// for case when returnUrl search param has its own search params
	// for example: ?returnUrl=/sports/full&eventId="qJPLWovgT4YoxRAV0MujH2"&sportId="5rkMRxXDl4zI2f3ROfxAx1"&isAdditionalLive=false&myBets=false&bb=""
	if (
		userStore.isLoggedIn &&
		returnUrl !== null &&
		numberOfParams > 1 &&
		next === RequiredUserAction.None
	) {
		let url = `/${getCurrentCulture()}${returnUrl}?`;

		Array.from(params.entries())
			.filter(
				([key]) =>
					!messageDisplayTypes.includes(key) && key !== "returnUrl"
			)
			.map(([key, value]) => (url += `&${[key]}=${value}`));

		// replaces first occurrence of &
		return <Redirect to={url.replace("&", "")} />;
	}

	if (
		userStore.isLoggedIn &&
		returnUrl !== null &&
		next === RequiredUserAction.None
	) {
		return <Redirect to={`/${getCurrentCulture()}${returnUrl}`} />;
	}

	if (
		doesRouteRequireUserAction &&
		next === RequiredUserAction.None &&
		userStore.isLoggedIn &&
		!pathname.includes(homeUrl)
	) {
		return <Redirect to={homeUrl} />;
	}

	if (privateRoute) {
		return (
			<PrivateRoute>
				<Route path={path} {...rest}>
					{children}
				</Route>
			</PrivateRoute>
		);
	}

	return (
		<Route path={path} {...rest}>
			{children}
		</Route>
	);
});
